"use client";
import Link from "next/link";
import Image from "next/image";
import { Button } from "@/components/atoms/button";
import { Divider } from "antd";
import { FieldGroup, Field, Label } from "@/components/atoms/fieldset";
import { InputPhone } from "@/components/molecules/phone";
import { PasswordInput } from "@/components/molecules/password-input";
import { Strong, Text } from "@/components/atoms/text";
import { useSignUpForm } from "../hooks/useSignUpForm";
import { FormProvider } from "react-hook-form";
import FieldErrorMessage from "@/components/molecules/error-message";
import { Input } from "@/components/atoms/input";

export function SignUpForm() {
  const {
    methods,
    errors,
    register,
    loadingSignUp,
    loadingGoogleSignUp,
    phoneNumberIsPreset,
    onSubmit,
    signUpWithGoogle,
    showGoogle,
    verifyOtp,
    timer,
    activeTermsVersion,
    activeTermsUrl,
  } = useSignUpForm();

  return (
    <FormProvider {...methods}>
      <form aria-label="form" onSubmit={onSubmit}>
        <FieldGroup className="!space-y-4">
          <Field>
            <Label>Phone number</Label>
            <InputPhone {...register("phone")} disabled={phoneNumberIsPreset} />
            <FieldErrorMessage error={errors?.phone} />
          </Field>
          <Field>
            <Label>Password</Label>
            <PasswordInput
              register={register("password")}
              placeholder="Enter your password"
              id="password"
            />
            <FieldErrorMessage error={errors?.password} />
          </Field>
          <Field>
            <div className="flex justify-between">
              <Label> Verification code</Label>
              {timer ? (
                <small>
                  Resend code in {timer < 10 ? `0${timer}` : timer} seconds
                </small>
              ) : null}
            </div>
            <div className="flex w-full gap-1 pt-2">
              <Input
                {...register("code")}
                placeholder="Enter the verification code"
                id="code"
              />
              <Button
                disabled={timer >= 1 && timer <= 30}
                full={false}
                className={"!w-32"}
                onClick={verifyOtp}>
                <span>Send code</span>
              </Button>
            </div>
            <FieldErrorMessage error={errors?.code} />
          </Field>
        </FieldGroup>
        <div className="mt-4 rounded-md border border-gray-300 bg-white p-3 text-sm">
          <label className="flex items-start gap-2">
            <input
              type="checkbox"
              {...register("acceptedTerms")}
              className="mt-1 h-4 w-4"
            />
            <span className="text-gray-700">
              I have read and agree to the latest Terms & Conditions.
              <Link
                href={
                  activeTermsUrl || "https://centihq.com/terms-and-conditions"
                }
                target="_blank"
                rel="noopener noreferrer"
                className="ml-1 font-medium underline text-dark">
                View Terms
              </Link>
              {!activeTermsVersion && (
                <span className="ml-1 text-red-600">
                  (Unable to load current terms)
                </span>
              )}
            </span>
          </label>
          <FieldErrorMessage error={errors?.acceptedTerms} />
        </div>
        <div className="w-full mt-6">
          <Button
            full
            className="!h-11"
            type="submit"
            loading={loadingSignUp}
            disabled={loadingSignUp}>
            Sign up
          </Button>
        </div>
        {showGoogle && (
          <>
            <Divider
              plain
              style={{ fontWeight: "500" }}
              className="animate-fadeIn duration-1000">
              or
            </Divider>
            <Button
              full
              onClick={signUpWithGoogle}
              className="!h-11 !rounded-lg animate-fadeIn duration-1000"
              loading={loadingGoogleSignUp}
              disabled={loadingGoogleSignUp}>
              <Image
                src="/images/google-icon.svg"
                width={15}
                height={15}
                alt="Google"
              />
              Sign up with Google
            </Button>
          </>
        )}
        <Text className="text-center pt-6">
          Already have an account?{" "}
          <Strong>
            <Link href="/login" className="hover:underline">
              Login
            </Link>
          </Strong>
        </Text>
      </form>
    </FormProvider>
  );
}
